import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import {
  GoogleMap,
  useLoadScript,
  OverlayView,
  Marker,
  MarkerClusterer,
  DirectionsRenderer,
} from "@react-google-maps/api";
import config from "../../config.js";
import {
  Typography,
  CircularProgress,
  Box,
  Divider,
  Select,
  MenuItem,
} from "@mui/material";
// import busIcon from "../../images/vehicle/Map_Vehicle_active.svg";
import RouteVehicleStatusTable from "./RouteVehicleStatusTable";
import { useNavigate } from "react-router-dom";
import NavMenu from "../../components/navbar/NavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import { useTranslation } from "react-i18next";
import MonitorSubtitle from "../../components/common/MonitorSubtitle";
import { getRightToLeftBusIcon } from "../stops/vehicleIcons.js";
import { getVehicleDirection } from "../../components/dashboard/dashboardAPI.js";
import { findNearestPointOnRoute } from "../../components/dashboard/mapUtils.js";
import Footer from "../../components/common/Footer.jsx";
import { useSettings } from "../../context/SettingsContext";
import { useMapIcons } from "../../hooks/useMapIcons.js";
import {
  computeVehicleLabelOffsets,
  computeStopLabelOffsets,
} from "../../utils/mapLabelOffsets.js";

const styles = {
  container: { padding: "32px" },
  cardSection: { marginBottom: "32px" },
  card: {
    padding: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: "12px",
    color: "white",
  },
  vehicleCard: { backgroundColor: "rgba(88, 0, 191, 1)" },
  stopCard: { backgroundColor: "rgba(0, 178, 191, 1)" },
  scheduleCard: { backgroundColor: "rgba(191, 80, 0, 1)" },
  cardContent: { display: "flex", flexDirection: "column" },
  icon: { fontSize: "40px", marginTop: "10px" },
  mapContainer: { width: "100%", height: "400px", marginBottom: "32px" },
  tableContainer: { marginTop: "32px" },
  table: { width: "100%", borderCollapse: "collapse", marginTop: "-7px" },
  tableHeader: {
    padding: "8px",
    textAlign: "left",
    backgroundColor: "#f5f5f5",
  },
  tableCell: { border: "1px solid #ddd", padding: "8px" },
  noData: { textAlign: "center", padding: "46px", height: "auto" },
  formHeader: { fontSize: "1.2rem", fontWeight: "bold", marginBottom: "16px" },
};

const mapStyles = [
  { featureType: "all", elementType: "all", stylers: [{ visibility: "off" }] },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "black" }],
  },
];

const libraries = ["geometry"];
const normalizeDeviceData = (data) => {
  let lat, lng, speed, heading, timestamp, rawData, altitude, satellites, temperature;

  // Handle first format (e.g., BIAL_SHUTTLE02)
  if (data.Latitude && data.Longitude) {
    lat = parseFloat(data.Latitude);
    lng = parseFloat(data.Longitude);
    speed = parseFloat(data.Speed) || 0;
    heading = parseFloat(data.Heading || data.heading || data.CourseAngle || 0);
    timestamp = data.timestamp
      ? new Date(data.timestamp).toISOString()
      : new Date().toISOString();
    rawData = data.RawData || "";
    const rawDataParts = rawData ? rawData.split(",") : [];
    altitude = rawDataParts[9] ? parseFloat(rawDataParts[9]) : null;
    satellites =
      data.UsedSatellites?.GPSSatellitesCount || rawDataParts[1] || "N/A";
    temperature = data.EnvironmentalData?.Temperature || "N/A";
  }
  // Handle second format (e.g., 357803371490877)
  else if (data.latitude && data.longitude) {
    lat = parseFloat(data.latitude);
    lng = parseFloat(data.longitude);
    speed = parseFloat(data.speed) || 0;
    heading = parseFloat(data.heading || data.Heading || data.CourseAngle || 0);
    timestamp = data.timestamp
      ? new Date(data.timestamp).toISOString()
      : new Date().toISOString();
    rawData = "";
    altitude = null;
    satellites = "N/A";
    temperature = "N/A";
  } else {
    console.warn(
      `Invalid data format for device ${data.device_id || "unknown"}`,
    );
    return null;
  }

  if (isNaN(lat) || isNaN(lng)) {
    console.warn(
      `Invalid coordinates for device ${data.device_id || "unknown"}`,
    );
    return null;
  }

  return {
    deviceId: data.device_id,
    latitude: lat.toString(),
    longitude: lng.toString(),
    timestamp,
    rawData,
    altitude,
    speed,
    heading: isNaN(heading) ? 0 : heading,
    satellites,
    temperature,
  };
};
const calculateDistanceAndETA = (
  vehiclePos,
  routePath,
  stops,
  vehicleSpeed,
) => {
  if (!routePath || routePath.length === 0) return { distance: 0, eta: 0 };

  const vehiclePoint = new window.google.maps.LatLng(
    vehiclePos.lat,
    vehiclePos.lng,
  );
  let minDistance = Infinity;
  let closestIndex = 0;

  routePath.forEach((point, index) => {
    const distance =
      window.google.maps.geometry.spherical.computeDistanceBetween(
        vehiclePoint,
        point,
      );
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  const remainingPath = routePath.slice(closestIndex);
  const totalDistance =
    remainingPath.reduce((acc, point, i) => {
      if (i === 0) return acc;
      return (
        acc +
        window.google.maps.geometry.spherical.computeDistanceBetween(
          remainingPath[i - 1],
          point,
        )
      );
    }, 0) / 1000; // Convert to km

  const avgSpeed = vehicleSpeed || 10; // Default speed in km/h if not available
  const timeInSeconds = totalDistance / avgSpeed + 60; // Convert to seconds

  let cumulativeDuration = 0;
  stops.forEach((stop, index) => {
    if (index >= closestIndex) {
      cumulativeDuration += parseInt(stop.durationValue || 60); // Default 1 min if not available
    }
  });

  return {
    distance: totalDistance.toFixed(2),
    eta: timeInSeconds + cumulativeDuration,
  };
};

const RouteTrackingComponent = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const Navigate = useNavigate();
  const [selectedStop, setSelectedStop] = useState(null);
  const routeData = location.state?.selectedRouteData;
  const [vehicleData, setVehicleData] = useState({});
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const [vehicleIds, setVehicleIds] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [directions, setDirections] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [vehicleStatuses, setVehicleStatuses] = useState({});
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const eventSourceRef = useRef(null);
  const routePathRef = useRef(null);
  const routeBoundsRef = useRef(null);
  const [zoom, setZoom] = useState(15);
  const [vehicleDirections, setVehicleDirections] = useState({});
  const [snappedPositions, setSnappedPositions] = useState({});
  const lastKnownHeadings = useRef({});
  const deviceIdToVehicles_color = useRef({}); // Map deviceId to vehicle color
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });


  const { settings } = useSettings();
  const { getOriginStopIcon, getDestinationStopIcon, getIntermediateStopIcon, getVehicleIcon: getCustomVehicleIcon } =
    useMapIcons(
      settings,
      routeData?.vehicles || [],
      routeData?.stops || [],
      routeData ? [routeData] : [],
    );

  const getVehicleNameByDeviceId = (deviceId) => {
    const vehicle = routeData?.vehicles?.find((v) => v.device_id === deviceId);
    return vehicle ? vehicle.name : deviceId;
  };

  // eslint-disable-next-line no-unused-vars
  const getVehicleColor = (deviceId) => {
    return deviceIdToVehicles_color.current[deviceId] || "#20A8E0";
  };

  // Populate deviceIdToVehicles_color — same as DashboardMap
  useEffect(() => {
    const colorMap = {};
    routeData?.vehicles?.forEach((vehicle) => {
      colorMap[vehicle.device_id] = vehicle.vehicle_color || "green";
    });
    deviceIdToVehicles_color.current = colorMap;
  }, [routeData?.vehicles]);

  // Local vehicle icon builder — mirrors useMapIcons getVehicleIcon rotation exactly
  const getLocalVehicleIcon = (deviceId, heading, direction = "unknown") => {
    const color = deviceIdToVehicles_color.current[deviceId] || "green";
    // Matches useMapIcons: finalHeading = heading + 90 (direction adjustment is handled server-side)
    const finalHeading = heading + 90;
    const rotatedSVG = getRightToLeftBusIcon(color, finalHeading).trim();
    const encodedSVG = encodeURIComponent(rotatedSVG);
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodedSVG}`,
      scaledSize: new window.google.maps.Size(45, 44),
      anchor: new window.google.maps.Point(22, 22),
    };
  };

  const getDirectionArrow = (deviceId) => {
    const direction = vehicleDirections[deviceId];
    if (!direction || direction === "unknown") return "";
    return direction === "source_to_dest" ? " ➡️" : " ⬅️";
  };

  const formatDistance = (distance) => {
    if (!distance) return "N/A";
    return `${parseFloat(distance).toFixed(1)} km`;
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return "N/A";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  useEffect(() => {
    if (routeData?.vehicles?.length > 0) {
      const ids = routeData?.vehicles?.map((vehicle) => vehicle.device_id);
      console.log("Vehicle IDs:", ids);
      setVehicleIds(ids);
    } else {
      console.warn("No vehicles found in routeData");
    }
  }, [routeData]);

  useEffect(() => {
    const updatedVehicleData = vehicleLocations.reduce((acc, location) => {
      if (location.deviceId && location.latitude && location.longitude) {
        const position = {
          lat: parseFloat(location.latitude),
          lng: parseFloat(location.longitude),
        };
        const { distance, eta } = calculateDistanceAndETA(
          position,
          routePathRef.current,
          routeData.stops,
          location.speed,
        );
        acc[location.deviceId] = {
          device_id: location.deviceId,
          position,
          timestamp: location.timestamp,
          speed: location.speed || 0,
          heading: location.heading || 0,
          altitude: location.altitude,
          satellites: location.satellites,
          temperature: location.temperature,
          distance,
          eta,
        };
      }
      return acc;
    }, {});
    console.log("Updated vehicleData:", updatedVehicleData);
    setVehicleData(updatedVehicleData);
    setLastUpdateTime(new Date().toLocaleString());
    setLoading(false);
  }, [vehicleLocations, routeData.stops]);

  const fetchAllVehicleLocations = useCallback(() => {
    if (vehicleIds.length === 0) return;

    const query = vehicleIds?.map((id) => `vehicleId=${id}`).join("&");
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    const sseUrl = `${config.baseURL}/redis/stream?${query}`;
    const source = new EventSource(sseUrl);
    eventSourceRef.current = source;

    source.onopen = () => console.log("SSE connection established");
    source.onerror = (error) => {
      console.error("SSE error:", error);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setTimeout(fetchAllVehicleLocations, 5000);
    };
    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("Received SSE data:", data);
        if (data.type === "connected") {
          console.log("SSE connected:", data);
          return;
        }
        if (
          data.device_id &&
          (data.Latitude || data.latitude) &&
          (data.Longitude || data.longitude)
        ) {
          const normalizedData = normalizeDeviceData(data);
          if (normalizedData) {
            setVehicleLocations((prev) => {
              const existingIndex = prev.findIndex(
                (item) => item.deviceId === normalizedData.deviceId,
              );
              if (existingIndex >= 0) {
                const newLocations = [...prev];
                newLocations[existingIndex] = normalizedData;
                return newLocations;
              }
              return [...prev, normalizedData];
            });
          }
        } else {
          console.warn("Unexpected SSE data format:", data);
        }
      } catch (e) {
        console.error("Error parsing SSE data:", e);
      }
    };
  }, [vehicleIds]);

  useEffect(() => {
    if (vehicleIds.length > 0) {
      fetchAllVehicleLocations();
      return () => {
        if (eventSourceRef.current) eventSourceRef.current.close();
      };
    }
  }, [vehicleIds, fetchAllVehicleLocations]);

  const center = useMemo(() => {
    if (routeData?.routeCoordinates?.[0]) {
      return {
        lat: parseFloat(routeData.routeCoordinates[0].latitude),
        lng: parseFloat(routeData.routeCoordinates[0].longitude),
      };
    }
    if (routeData?.stops?.[0]) {
      return {
        lat: parseFloat(routeData.stops[0].latitude),
        lng: parseFloat(routeData.stops[0].longitude),
      };
    }
    return { lat: 13.1986, lng: 77.7099 };
  }, [routeData]);

  const stopMarkers = useMemo(() => {
    return routeData
      ? routeData?.stops?.map((stop) => ({
          position: {
            lat: parseFloat(stop.latitude),
            lng: parseFloat(stop.longitude),
          },
          label: stop.name || "Unknown",
        }))
      : [];
  }, [routeData]);

  const calculateRouteDirections = useCallback(() => {
    if (!window.google || !routeData?.routeCoordinates?.length) return;
    const directionsService = new window.google.maps.DirectionsService();
    const origin = {
      lat: parseFloat(routeData.routeCoordinates[0].latitude),
      lng: parseFloat(routeData.routeCoordinates[0].longitude),
    };
    const destination = {
      lat: parseFloat(
        routeData.routeCoordinates[routeData.routeCoordinates.length - 1]
          .latitude,
      ),
      lng: parseFloat(
        routeData.routeCoordinates[routeData.routeCoordinates.length - 1]
          .longitude,
      ),
    };
    const waypoints = routeData.routeCoordinates?.slice(1, -1)?.map((coord) => {
      const isStop = routeData.stops.some(
        (stop) =>
          parseFloat(stop.latitude) === parseFloat(coord.latitude) &&
          parseFloat(stop.longitude) === parseFloat(coord.longitude),
      );
      return {
        location: new window.google.maps.LatLng(
          parseFloat(coord.latitude),
          parseFloat(coord.longitude),
        ),
        stopover: isStop,
      };
    });
    const request = {
      origin: new window.google.maps.LatLng(origin.lat, origin.lng),
      destination: new window.google.maps.LatLng(
        destination.lat,
        destination.lng,
      ),
      waypoints,
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    };
    directionsService.route(request, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        console.log("Directions received:", result);
        setDirections(result);
        routePathRef.current = result.routes[0].overview_path;
        if (mapInstance && !routeBoundsRef.current) {
          const bounds = new window.google.maps.LatLngBounds();
          result.routes[0].overview_path.forEach((point) =>
            bounds.extend(point),
          );
          stopMarkers.forEach((marker) => bounds.extend(marker.position));
          routeBoundsRef.current = bounds;
          mapInstance.fitBounds(bounds);
          window.google.maps.event.addListenerOnce(mapInstance, "idle", () => {
            const currentZoom = mapInstance.getZoom();
            if (currentZoom > 15) mapInstance.setZoom(15);
            else if (currentZoom < 10) mapInstance.setZoom(10);
          });
        }
      } else {
        console.error("Directions request failed:", status);
        setError(`Error fetching directions: ${status}`);
      }
    });
  }, [routeData?.routeCoordinates, routeData?.stops, mapInstance, stopMarkers]);

  const onLoad = useCallback(
    (map) => {
      map.setCenter(center);
      map.setZoom(17);
      setMapLoaded(true);
      setMapInstance(map);
    },
    [center],
  );

  // ─── Pixel-space label collision offsets ─────────────────────────────────────
  const labelOffsets = useMemo(() => {
    if (!mapInstance) return {};
    const projection = mapInstance.getProjection();
    if (!projection) return {};
    const entries = vehicleLocations.map((v) => {
      const vData = routeData?.vehicles?.find((vv) => vv.device_id === v.deviceId) || {};
      const direction = vehicleDirections[v.deviceId];
      const hasArrow = !!(direction && direction !== "unknown");
      return {
        deviceId: v.deviceId,
        lat: parseFloat(v.latitude),
        lng: parseFloat(v.longitude),
        name: vData.name || "",
        hasArrow,
      };
    });
    return computeVehicleLabelOffsets(projection, zoom, entries, { BASE_OFFSET: 30 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleLocations, zoom, mapInstance, vehicleDirections]);

  // ─── Stop label collision offsets ─────────────────────────────────────────
  const stopLabelOffsets = useMemo(() => {
    if (!mapInstance || !stopMarkers.length) return {};
    const projection = mapInstance.getProjection();
    if (!projection) return {};
    const entries = stopMarkers.map((marker, idx) => ({
      id: idx,
      lat: marker.position.lat,
      lng: marker.position.lng,
      name: marker.label || "",
    }));
    const raw = computeStopLabelOffsets(projection, zoom, entries, {
      LABEL_H: 24,
      BASE_OFFSET: 62,
    });
    const offsets = {};
    Object.entries(raw).forEach(([key, val]) => {
      offsets[key] = val.topPx;
    });
    return offsets;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopMarkers, zoom, mapInstance]);

  // ─── Fetch vehicle directions from Redis ─────────────────────────────────────
  useEffect(() => {
    if (!vehicleIds.length) return;
    const fetchDirections = async () => {
      const dirs = {};
      await Promise.all(
        vehicleIds.map(async (deviceId) => {
          const dir = await getVehicleDirection(deviceId);
          if (dir) dirs[deviceId] = dir;
        }),
      );
      setVehicleDirections((prev) => ({ ...prev, ...dirs }));
    };
    fetchDirections();
    const interval = setInterval(fetchDirections, 30000);
    return () => clearInterval(interval);
  }, [vehicleIds]);

  // ─── Snap vehicles to route + heading ────────────────────────────────────────
  useEffect(() => {
    if (!directions || !vehicleLocations.length) return;
    const newSnapped = {};
    vehicleLocations.forEach((loc) => {
      const position = { lat: parseFloat(loc.latitude), lng: parseFloat(loc.longitude) };
      const snapped = findNearestPointOnRoute(position, directions);
      if (snapped) newSnapped[loc.deviceId] = snapped;
    });
    setSnappedPositions(newSnapped);
  }, [vehicleLocations, directions]);

  useEffect(() => {
    if (mapLoaded && routeData && mapInstance) {
      calculateRouteDirections();
    }
  }, [mapLoaded, routeData, mapInstance, calculateRouteDirections]);

  useEffect(() => {
    if (
      mapInstance &&
      routeBoundsRef.current &&
      Object.values(vehicleData).length > 0
    ) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.union(routeBoundsRef.current);
      stopMarkers.forEach((marker) => bounds.extend(marker.position));
      Object.values(vehicleData).forEach((vehicle) =>
        bounds.extend(vehicle.position),
      );
      mapInstance.fitBounds(bounds);
      window.google.maps.event.addListenerOnce(mapInstance, "idle", () => {
        const currentZoom = mapInstance.getZoom();
        if (currentZoom > 15) mapInstance.setZoom(15);
        else if (currentZoom < 10) mapInstance.setZoom(10);
      });
    }
  }, [vehicleData, mapInstance, stopMarkers]);

  const routeDetailsUseriew = () => {
    try {
      console.log("Original routeData:", routeData);

      if (!routeData) {
        console.error("No route data available");
        return;
      }

      const routeDataForTracking = {
        id: routeData.id,
        name: routeData.name,
        routeId: routeData.routeId,
        stops: routeData.stops || [],
        vehicles: routeData.vehicles || [],
        routeCoordinates: routeData.routeCoordinates || [],
        schedules: routeData.schedules || [],
      };

      console.log("Prepared routeDataForTracking:", routeDataForTracking);

      Navigate(`/route/screen/${routeData.id}`, {
        state: {
          selectedRouteData: routeDataForTracking,
        },
      });
    } catch (error) {
      console.error("Error in routeDetailsUseriew:", error);
    }
  };

  const handleStopChange = (e) => {
    const stopId = e.target.value;

    if (stopId === "") {
      setSelectedStop(null);
      if (mapInstance && routeBoundsRef.current) {
        mapInstance.fitBounds(routeBoundsRef.current);
        window.google.maps.event.addListenerOnce(mapInstance, "idle", () => {
          const currentZoom = mapInstance.getZoom();
          if (currentZoom > 15) mapInstance.setZoom(15);
          else if (currentZoom < 10) mapInstance.setZoom(10);
        });
      }
    } else {
      const stop = routeData.stops.find((stop) => stop.id === stopId);
      if (stop) {
        setSelectedStop(stop);
        if (mapInstance) {
          const position = {
            lat: parseFloat(stop.latitude),
            lng: parseFloat(stop.longitude),
          };
          mapInstance.panTo(position);
          mapInstance.setZoom(17);
        }
      }
    }
  };

  if (!routeData) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <div style={styles.noData}>
          <Typography variant="h6">No route data available</Typography>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <div style={styles.noData}>
          <Typography color="error">{error}</Typography>
        </div>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <div style={styles.noData}>
          <Typography color="error">Failed to load Google Maps</Typography>
        </div>
      </>
    );
  }
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  if (!isLoaded) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <div style={styles.noData}>
          <CircularProgress />
        </div>
      </>
    );
  }

  return (
    <>
      <TitleBar />
      <NavMenu />
      <MonitorSubtitle
        title="Monitor Route"
        path={`${orgName || "organization"} > Routes > ${routeData.name} > Monitor`}
        track={"Route View"}
        onTrackClick={() => routeDetailsUseriew()}
      />
      <Box
        sx={{
          borderRadius: "10px",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          padding: { xs: "15px", sm: "15px 30px", md: "20px 60px" },
          backgroundColor: "#FFFFFF",
          border: "1px solid #A5DCF3",
          margin: { xs: "0px 15px", sm: "0px 20px", md: "0px 40px" },
          boxShadow: "-2px 1px 5px 1px #00000040",
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
            mb: { xs: 2, sm: 0 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059",
              }}
            >
              {t("Total Vehicles")}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "20px", md: "24px" },
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
            }}
          >
            {routeData.vehicles.length}
          </Typography>
        </Box>
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 },
            height: { xs: "1px", sm: "60px" },
            width: { xs: "100%", sm: "auto" },
            alignSelf: "center",
            border: "1px solid #183059",
          }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
            mb: { xs: 2, sm: 0 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059",
              }}
            >
              {t("Total Stops")}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "20px", md: "24px" },
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
            }}
          >
            {routeData.stops.length}
          </Typography>
        </Box>
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 },
            height: { xs: "1px", sm: "60px" },
            width: { xs: "100%", sm: "auto" },
            alignSelf: "center",
            border: "1px solid #183059",
          }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059",
              }}
            >
              {t("Total Schedules")}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "20px", md: "24px" },
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
              }}
            >
              {routeData.schedules.length}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          borderRadius: "10px",
          padding: { xs: "10px", sm: "15px", md: "20px" },
          backgroundColor: "#FFFFFF",
          border: "1px solid #A5DCF3",
          margin: { xs: "10px 15px", sm: "15px 20px", md: "20px 40px" },
          boxShadow: "-2px 1px 5px 1px #00000040",
        }}
      >
        <Box
          sx={{
            minHeight: "48px",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            padding: { xs: "12px", sm: "12px 16px" },
            background: "#E8EAEE",
            boxShadow: "0px 2px 5px 0px #00000040",
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Typography
              sx={{
                color: "#414958",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%",
              }}
            >
              {t("Route")}:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 600,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                color: "#183059",
                letterSpacing: "0%",
                ml: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {routeData.name || "---"}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Select
              value={selectedStop ? selectedStop.id : ""}
              onChange={handleStopChange}
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
              sx={{
                height: "30px",
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" },
              }}
            >
              <MenuItem value="">{t("Stops")}</MenuItem>
              {routeData?.stops?.map((stop) => (
                <MenuItem key={stop.id} value={stop.id}>
                  {stop.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "400px",
            border: "1px solid #000000",
            marginTop: "20px",
            borderRadius: "20px",
          }}
          center={center}
          onLoad={onLoad}
          options={{ minZoom: 13, maxZoom: 30, styles: mapStyles, zoom: 15 }}
          onZoomChanged={() => {
            if (mapInstance) setZoom(mapInstance.getZoom());
          }}
        >
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: routeData.routeColor || "#0000FF",
                  strokeOpacity: 0.8,
                  strokeWeight: 4,
                },
              }}
            />
          )}
          <MarkerClusterer
            options={(() => {
              const stopIcon = getIntermediateStopIcon();
              const clusterStyle = stopIcon
                ? {
                    url: stopIcon.url,
                    width: 40,
                    height: 40,
                    textColor: "#000000",
                    textSize: 11,
                    fontWeight: "bold",
                    anchorIcon: [40, 20],
                    anchorText: [-2, 0],
                  }
                : {
                    url:
                      "data:image/svg+xml;charset=UTF-8," +
                      encodeURIComponent(
                        `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                          <circle cx="20" cy="20" r="18" fill="#d32f2f" stroke="#fff" stroke-width="2"/>
                        </svg>`,
                      ),
                    width: 40,
                    height: 40,
                    textColor: "#ffffff",
                    textSize: 11,
                    fontWeight: "bold",
                    anchorIcon: [20, 20],
                    anchorText: [0, 0],
                  };
              return {
                styles: [clusterStyle, clusterStyle, clusterStyle],
                gridSize: 20,
                minimumClusterSize: 2,
                zoomOnClick: true,
              };
            })()}
          >
            {(clusterer) =>
              stopMarkers?.map((marker, index) => {
                const offsetPx = stopLabelOffsets[index] ?? 62;
                const connectorH = offsetPx - 26;
                return (
                <React.Fragment key={index}>
                  <OverlayView
                    position={marker.position}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                <div style={{ position: "relative", top: "0px", left: "0px" }}>
                  <div
                    style={{
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                      bottom: `${offsetPx}px`,
                      backgroundColor:
                        selectedStop && marker.label === selectedStop.name
                          ? "#ffff00"
                          : "white",
                      color:
                        index === 0 || index === stopMarkers.length - 1
                          ? "green"
                          : "black",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize:
                        index === 0 || index === stopMarkers.length - 1
                          ? "12px"
                          : "10px",
                      fontWeight:
                        selectedStop && marker.label === selectedStop.name
                          ? "bold"
                          : index === 0 || index === stopMarkers.length - 1
                            ? "700"
                            : "500",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      whiteSpace: "nowrap",
                      zIndex: 1000,
                    }}
                  >
                    {marker.label}
                  </div>
                  {connectorH > 0 && (
                    <svg
                      style={{
                        position: "absolute",
                        left: "50%",
                        transform: "translateX(-50%)",
                        bottom: "26px",
                        pointerEvents: "none",
                        overflow: "visible",
                        zIndex: 999,
                      }}
                      width="2"
                      height={connectorH}
                    >
                      <line
                        x1="1" y1="0" x2="1" y2={connectorH}
                        stroke="rgba(0,0,0,0.4)"
                        strokeWidth="1.5"
                        strokeDasharray="3 2"
                      />
                    </svg>
                  )}
                </div>
              </OverlayView>
                  <Marker
                    position={marker.position}
                    clusterer={clusterer}
                    icon={
                      selectedStop && marker.label === selectedStop.name
                        ? {
                            url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                            scaledSize: new window.google.maps.Size(40, 40),
                          }
                        : (() => {
                            const isOrigin = index === 0;
                            const isDestination = index === stopMarkers.length - 1;
                            let customIcon = null;
                            if (isOrigin) customIcon = getOriginStopIcon();
                            else if (isDestination)
                              customIcon = getDestinationStopIcon();
                            else customIcon = getIntermediateStopIcon();
                            return (
                              customIcon ||
                              (isOrigin || isDestination
                                ? {
                                    url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
                                    scaledSize: new window.google.maps.Size(40, 40),
                                  }
                                : undefined)
                            );
                          })()
                    }
                  />
                </React.Fragment>
                );
              })
            }
          </MarkerClusterer>

          {vehicleLocations?.map((vehicle) => {
            const vehicleInfo = routeData?.vehicles?.find((v) => v.device_id === vehicle?.deviceId) || {};
            const deviceId = vehicle?.deviceId;
            const direction = vehicleDirections[deviceId];
            const isOffRoute = !snappedPositions[deviceId];
            const status = vehicleStatuses[deviceId] || vehicleInfo?.status;
            const heading = vehicle?.heading || lastKnownHeadings.current[deviceId] || 0;
            if (vehicle?.heading && vehicle.heading !== 0) {
              lastKnownHeadings.current[deviceId] = vehicle.heading;
            }
            // Mirror DashboardMap: getCustomVehicleIcon with zoom, fallback to local icon
            const vehicleIcon = getCustomVehicleIcon(deviceId, heading, direction, zoom) ||
              getLocalVehicleIcon(deviceId, heading, direction);
            const position = vehicleData[deviceId]?.position || {
              lat: parseFloat(vehicle.latitude),
              lng: parseFloat(vehicle.longitude),
            };
            const bgColor =
              status === "DEAD" || status === "INACTIVE"
                ? "#F9E719"
                : isOffRoute
                  ? "#cf13b9ff"
                  : "#008C23";
            const textColor =
              status === "DEAD" || status === "INACTIVE" ? "#000" : "#fff";

            return (
              vehicle &&
              vehicle.latitude &&
              vehicle.longitude && (
              <React.Fragment key={deviceId}>
                <OverlayView
                  position={position}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    style={{
                      position: "relative",
                      top: "0px",
                      left: "0px",
                      width: "2px",
                      height: "2px",
                    }}
                  >
                    {(() => {
                      const topPx = labelOffsets[deviceId]?.topPx ?? 30;
                      const connectorH = topPx;
                      return (
                        <>
                          {connectorH > 4 && (
                            <svg
                              style={{
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                                top: "0px",
                                pointerEvents: "none",
                                overflow: "visible",
                                zIndex: 999,
                              }}
                              width="2"
                              height={connectorH}
                            >
                              <line
                                x1="1"
                                y1="0"
                                x2="1"
                                y2={connectorH}
                                stroke="black"
                                strokeWidth="1.8"
                                strokeDasharray="3 2"
                              />
                            </svg>
                          )}
                          <div
                            style={{
                              position: "absolute",
                              top: labelOffsets[deviceId]?.top ?? "30px",
                              left: labelOffsets[deviceId]?.left ?? "50%",
                              transform: labelOffsets[deviceId]?.transform ?? "translateX(-50%)",
                              color: textColor,
                              fontSize: "10px",
                              fontWeight: 600,
                              fontFamily: "Arial, sans-serif",
                              lineHeight: "14.06px",
                              padding: "8px",
                              textAlign: "left",
                              whiteSpace: "nowrap",
                              backgroundColor: bgColor,
                              borderRadius: "12px",
                              zIndex: 1000,
                              border: "1px solid black",
                            }}
                          >
                            {getVehicleNameByDeviceId(deviceId)}
                            <span style={{ fontWeight: "bold" }}>
                              {getDirectionArrow(deviceId)}
                            </span>
                            {isOffRoute && <span> (Off-Route)</span>}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </OverlayView>
                <Marker
                  position={position}
                  icon={vehicleIcon}
                  zIndex={1000}
                />
              </React.Fragment>
              )
            );
          })}
        </GoogleMap>
      </Box>
      <RouteVehicleStatusTable
        loading={loading}
        error={error}
        vehicleData={vehicleData}
        formatDistance={formatDistance}
        formatTime={formatTime}
        getVehicleNameByDeviceId={getVehicleNameByDeviceId}
      />

      {lastUpdateTime && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "right",
            mt: 1,
            color: "text.secondary",
          }}
        >
          Last updated: {lastUpdateTime}
        </Typography>
      )}
      <Footer />
    </>
  );
};

export default RouteTrackingComponent;
